import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { blockUI, displayMessage, unblockUI } from '../../core_web/store/Application.store';
import ClientFactory from '../api/ApiFactory';

import {
  AcaoSolicitacaoPagamentoFrete,
  FiltroSolicitacaoPagamentoFrete,
  HistoricoSolicitacaoPagamentoFreteResponseResult,
  ObservacaoPagamentoFreteResponseListResult,
  SolicitacaoPagamentoFreteAnexoResponseResult,
  SolicitacaoPagamentoFreteResponseListResult,
  SolicitacaoPagamentoFreteUpdate
} from '../api/lar.logistica.api';
import { AppDispatch, AppThunk } from './index';
import { ObterSolicitacaoDto } from '../interfaces/global';
import { documentUtils } from '../helpers';

const SolicitacaoPagamento = createSlice({
  name: 'SolicitacaoPagamento',
  initialState: {
    listSolicitacaoPagamento: {} as SolicitacaoPagamentoFreteResponseListResult,
    historioSolicitacoes: {} as HistoricoSolicitacaoPagamentoFreteResponseResult,
    solicitacaoWithAnexos: {} as SolicitacaoPagamentoFreteAnexoResponseResult,
    isLoadingAnexos: false,
    observacoes: {} as ObservacaoPagamentoFreteResponseListResult,
    isLoadingObservacoes: false
  },
  reducers: {
    setlistSolicitacaoPagamento(
      state,
      action: PayloadAction<SolicitacaoPagamentoFreteResponseListResult>
    ) {
      state.listSolicitacaoPagamento = action.payload;
    },
    setHistoricoSolicitacoes(
      state,
      action: PayloadAction<HistoricoSolicitacaoPagamentoFreteResponseResult>
    ) {
      state.historioSolicitacoes = action.payload;
    },
    resetData(state) {
      state.listSolicitacaoPagamento = {} as SolicitacaoPagamentoFreteResponseListResult;
      state.historioSolicitacoes = {} as HistoricoSolicitacaoPagamentoFreteResponseResult;
    },
    setSolicitacaoWithAnexos(
      state,
      action: PayloadAction<SolicitacaoPagamentoFreteAnexoResponseResult>
    ) {
      state.solicitacaoWithAnexos = action.payload;
    },
    resetSolicitacaoWithAnexos(state) {
      state.solicitacaoWithAnexos = {} as SolicitacaoPagamentoFreteAnexoResponseResult;
    },
    removeSolicitacaoAnexo(state, action: PayloadAction<string>) {
      const anexoId = action.payload;
      if (state.solicitacaoWithAnexos.data) {
        state.solicitacaoWithAnexos.data.anexos = state.solicitacaoWithAnexos.data.anexos?.filter(
          (anexo) => anexo.id !== anexoId
        );
      }
    },
    setLoadingAnexos(state, action: PayloadAction<boolean>) {
      state.isLoadingAnexos = action.payload;
    },
    setObservacoes(state, action: PayloadAction<ObservacaoPagamentoFreteResponseListResult>) {
      state.observacoes = action.payload;
    },
    setLoadingObservacoes(state, action: PayloadAction<boolean>) {
      state.isLoadingObservacoes = action.payload;
    }
  }
});

export const {
  setlistSolicitacaoPagamento,
  setHistoricoSolicitacoes,
  resetData,
  setSolicitacaoWithAnexos,
  resetSolicitacaoWithAnexos,
  removeSolicitacaoAnexo,
  setLoadingAnexos,
  setObservacoes,
  setLoadingObservacoes
} = SolicitacaoPagamento.actions;

export default SolicitacaoPagamento.reducer;

export function getObservacoesSolicitacao(): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(setLoadingObservacoes(true));
    const client = ClientFactory.GetClient();
    client
      .apiV1SolicitacaoPagamentosObservacoesGet()
      .then((result) => {
        dispatch(setObservacoes(result.toJSON()));
      })
      .catch((err) => {
        const errorMessage = documentUtils.extractErrorMessage(err);
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Não foi possivel obter as solicitações de pagamento: ${errorMessage}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(setLoadingObservacoes(false)));
  };
}

export function deleteAnexoSolicitacao(anexoId: string, solicitacaoId: string): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());
    const client = ClientFactory.GetClient();

    client
      .apiV1SolicitacaoPagamentosAnexosDelete(anexoId, solicitacaoId)
      .then(() => {
        dispatch(removeSolicitacaoAnexo(anexoId));
      })
      .catch((err) => {
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Erro ao deletar documento: ${err.message}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(unblockUI()));
  };
}

export function getAnexosSolicitacao(
  matricula: number,
  contrato: number,
  nota: number,
  cte: number
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(setLoadingAnexos(true));
    const client = ClientFactory.GetClient();
    client
      .apiV1SolicitacaoPagamentosAnexosGet(matricula, contrato, nota, cte)
      .then((result) => {
        dispatch(setSolicitacaoWithAnexos(result.toJSON()));
      })
      .catch((err) => {
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Não foi possivel obter as solicitações de pagamento: ${err.message}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(setLoadingAnexos(false)));
  };
}

export function viewAnexoSolicitacao(
  anexoId: string,
  cteLink: string | undefined,
  fileName: string | undefined,
  contentType: string | undefined,
  solicitacaoId: string
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());
    const client = ClientFactory.GetClient();

    if (cteLink) {
      window.open(cteLink, '_blank');
      return;
    }

    client
      .apiV1SolicitacaoPagamentosAnexosDownload(anexoId, solicitacaoId)
      .then((result) => {
        if (result.data) {
          const blob = result.data;
          const fileExtension = fileName?.split('.').pop()?.toLowerCase();
          const shouldViewInBrowser = shouldOpenInBrowser(contentType!, fileExtension);

          if (shouldViewInBrowser) {
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          } else {
            downloadFile(blob, fileName!);
          }
        }
      })
      .catch((err) => {
        const errorMessage = documentUtils.extractErrorMessage(err);
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Erro ao visualizar documento: ${errorMessage}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(unblockUI()));
  };
}

function shouldOpenInBrowser(contentType: string, fileExtension?: string): boolean {
  const viewableTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png'];

  return (
    viewableTypes.includes(contentType) ||
    (fileExtension ? viewableExtensions.includes(fileExtension) : false)
  );
}

function downloadFile(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadAnexoSolicitacao(
  anexoId: string,
  cteLink: string | undefined,
  fileName: string | undefined,
  contentType: string | undefined,
  solicitacaoId: string
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());
    const client = ClientFactory.GetClient();

    if (cteLink) {
      // Para links CTe, abrir em nova aba
      window.open(cteLink, '_blank');
      dispatch(unblockUI());
      return;
    }

    client
      .apiV1SolicitacaoPagamentosAnexosDownload(anexoId, solicitacaoId)
      .then((result) => {
        if (result.data) {
          const blob = result.data;
          const downloadFileName = fileName || `documento_${anexoId}`;

          // Fazer download do arquivo
          downloadFile(blob, downloadFileName);
        }
      })
      .catch((err) => {
        const errorMessage = documentUtils.extractErrorMessage(err);
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Erro ao fazer download do documento: ${errorMessage}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(unblockUI()));
  };
}

export function getSolicitacoes(
  matricula: number,
  dataInicial?: Date | undefined,
  contrato?: number | undefined,
  ufIntraMunicipal?: string | undefined,
  nota?: number | undefined,
  cte?: number | undefined,
  status?: FiltroSolicitacaoPagamentoFrete | undefined
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());
    const client = ClientFactory.GetClient();
    client
      .apiV1SolicitacaoPagamentosGet(
        matricula,
        dataInicial,
        contrato,
        ufIntraMunicipal,
        nota,
        cte,
        status
      )
      .then((result) => {
        dispatch(setlistSolicitacaoPagamento(result.toJSON()));
      })
      .catch((err) => {
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Não foi possivel obter as solicitações de pagamento: ${err.message}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(unblockUI()));
  };
}

export function getSolicitacoesPagasOrAguardando(
  matriculas: string,
  dataInicial: Date,
  dataFinal: Date,
  contrato?: number | undefined,
  nota?: number | undefined,
  cteNfse?: number | undefined,
  serieCteNfse?: string | undefined,
  status?: FiltroSolicitacaoPagamentoFrete | undefined,
  ufIntraMunicipal?: string | undefined,
  tipoContrato?: string
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());
    const client = ClientFactory.GetClient();
    client
      .apiV1SolicitacaoPagamentosDex(
        matriculas,
        dataInicial,
        dataFinal,
        contrato,
        nota,
        cteNfse,
        serieCteNfse,
        status,
        ufIntraMunicipal,
        tipoContrato
      )
      .then((result) => {
        dispatch(setlistSolicitacaoPagamento(result.toJSON()));
      })
      .catch((err) => {
        const errorMessage = documentUtils.extractErrorMessage(err);
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Não foi possivel obter as solicitações de pagamento: ${errorMessage}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(unblockUI()));
  };
}

function translateErrorMessage(message: string): string {
  if (message.includes('Required input cte cannot be zero or negative')) {
    return 'O valor do CTe/NFS-e não pode ser zero ou negativo. Por favor, insira um valor válido.';
  }
  if (message.includes('Contate o suporte')) {
    return 'Por favor, tente novamente. Se o problema persistir, contate o suporte técnico.';
  }
  return message;
}

export function putSolicitacao(
  matricula: number,
  contrato: number,
  nota: number,
  serie: string,
  acao: AcaoSolicitacaoPagamentoFrete,
  payload: SolicitacaoPagamentoFreteUpdate,
  recaptcha: string,
  solicitacaoDto: ObterSolicitacaoDto,
  ticketFile: File | null,
  nfseFile: File | null,
  cteFile: File | null,
  cteNfse: number,
  cteLink: string | null,
  nfseXmlFile?: File | null,
  observacao?: string | null,
  shouldCallSicop: boolean = true
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());

    // Validation for cteNfse
    if (!cteNfse || cteNfse <= 0) {
      dispatch(
        displayMessage({
          message: 'O campo CTe/NFS-e é obrigatório e deve ser um número positivo.',
          severity: 'error'
        })
      );
      dispatch(unblockUI());
      return;
    }

    const client = ClientFactory.GetClient();

    try {
      const solicitacaoWithAnexoResult = await client.apiV1SolicitacaoPagamentosAnexosPost(
        undefined,
        matricula,
        contrato,
        nota,
        cteNfse,
        documentUtils.convertToFileParameter(nfseFile),
        documentUtils.convertToFileParameter(cteFile),
        documentUtils.convertToFileParameter(ticketFile),
        documentUtils.convertToFileParameter(nfseXmlFile),
        cteLink || undefined,
        observacao || undefined
      );

      dispatch(setSolicitacaoWithAnexos(solicitacaoWithAnexoResult.toJSON()));

      if (shouldCallSicop) {
        const sanitizedPayload = SolicitacaoPagamentoFreteUpdate.fromJS({
          ...payload,
          serieCte: payload.serieCte?.replace(/\s/g, '')
        });
        const putResult = await client.apiV1SolicitacaoPagamentosPut(
          matricula,
          contrato,
          nota,
          serie.replace(/\s/g, ''),
          acao,
          sanitizedPayload
        );

        if (putResult?.data) {
          dispatch(setlistSolicitacaoPagamento(putResult.toJSON()));
        } else {
          const listResult = await client.apiV1SolicitacaoPagamentosGet(
            solicitacaoDto.matricula,
            solicitacaoDto.dataInicial,
            solicitacaoDto.contrato,
            solicitacaoDto.ufIntraMunicipal,
            solicitacaoDto.nota,
            solicitacaoDto.cte,
            solicitacaoDto.status
          );
          dispatch(setlistSolicitacaoPagamento(listResult.toJSON()));
        }
      }
    } catch (err: any) {
      const rawErrorMessage = documentUtils.extractErrorMessage(err);
      const translatedMessage = translateErrorMessage(rawErrorMessage);
      dispatch(
        displayMessage({
          message: `Erro ao alterar a solicitação de pagamento: ${translatedMessage}`,
          severity: 'error'
        })
      );
    } finally {
      dispatch(unblockUI());
    }
  };
}

export function getHistoricoSolicitacao(
  matricula: number,
  contrato: number,
  nota: number,
  serie: number
): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(blockUI());
    const client = ClientFactory.GetClient();
    client
      .apiV1SolicitacaoPagamentosHistorico(matricula, contrato, nota, serie)
      .then((result) => {
        dispatch(setHistoricoSolicitacoes(result.toJSON()));
      })
      .catch((err) => {
        dispatch(
          displayMessage({
            message:
              err.message === 'Unauthorized'
                ? 'Não Autorizado, Faça novamente o login'
                : `Não foi possivel obter o historico de solicitações de pagamento: ${err.message}`,
            severity: 'error'
          })
        );
      })
      .finally(() => dispatch(unblockUI()));
  };
}

export function clearData(): AppThunk {
  return async function (dispatch: AppDispatch | any | any) {
    dispatch(resetData());
  };
}
